import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { api, ApiError } from '../api/client';
import type { AccurateDatabase, Tenant, User } from '../api/types';

interface SessionValue {
  user: User | null;
  tenant: Tenant | null;
  databases: AccurateDatabase[];
  /** The Accurate database currently being worked in. */
  activeDatabase: AccurateDatabase | null;
  loading: boolean;
  selectDatabase: (id: string | null) => void;
  refresh: () => Promise<void>;
  refreshDatabases: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

const ACTIVE_DB_KEY = 'dr_inbound_active_db';

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [databases, setDatabases] = useState<AccurateDatabase[]>([]);
  const [activeDatabaseId, setActiveDatabaseId] = useState<string | null>(() => {
    // Remembering the last database is a convenience only; every request is
    // re-authorised server-side, so a stale value here grants nothing.
    try {
      return localStorage.getItem(ACTIVE_DB_KEY);
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);

  const refreshDatabases = useCallback(async () => {
    try {
      const data = await api.get<{ databases: AccurateDatabase[] }>('/accurate/databases');
      setDatabases(data.databases);
    } catch {
      setDatabases([]);
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const data = await api.get<{ user: User; tenant: Tenant }>('/me');
      setUser(data.user);
      setTenant(data.tenant);
      await refreshDatabases();
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setUser(null);
        setTenant(null);
        setDatabases([]);
      }
    } finally {
      setLoading(false);
    }
  }, [refreshDatabases]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const selectDatabase = useCallback((id: string | null) => {
    setActiveDatabaseId(id);
    try {
      if (id) localStorage.setItem(ACTIVE_DB_KEY, id);
      else localStorage.removeItem(ACTIVE_DB_KEY);
    } catch {
      // Private browsing or blocked storage: the choice simply does not persist.
    }
  }, []);

  const login = useCallback(
    async (email: string, password: string) => {
      const data = await api.post<{ user: User }>('/login', { email, password });
      setUser(data.user);
      await refresh();
    },
    [refresh],
  );

  const logout = useCallback(async () => {
    await api.post('/logout');
    setUser(null);
    setTenant(null);
    setDatabases([]);
    selectDatabase(null);
  }, [selectDatabase]);

  const activeDatabase = useMemo(
    () => databases.find((d) => d.id === activeDatabaseId) ?? null,
    [databases, activeDatabaseId],
  );

  const value = useMemo<SessionValue>(
    () => ({
      user,
      tenant,
      databases,
      activeDatabase,
      loading,
      selectDatabase,
      refresh,
      refreshDatabases,
      login,
      logout,
    }),
    [user, tenant, databases, activeDatabase, loading, selectDatabase, refresh, refreshDatabases, login, logout],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used inside SessionProvider');
  return ctx;
}
